import { ResourceSearchCardProps } from "@/common/card-types.ts";
import { AppLibraryResultItem } from "@modules/resource-search/resource-search-types.ts";
import { isEmpty } from "@utils/var.check.ts";
import SecurityUtils from "@utils/security.utils.ts";
import StringUtils from "@utils/string.utils.ts";
import AppListApi from "@apis/app.list.api.ts";
import { useRouting } from "@plugins/common/composables/routing.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import AppDetailContentSubpage from "@modules/app-list/components/AppDetailContentSubpage.vue";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useRouter } from "vue-router";
import { RESOURCE_SEARCH_TYPE } from "@modules/resource-search/resource-search-helper.ts";
import { useI18n } from "vue-i18n";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";

export const useAppSearchCard = () => {
  const { handleRouteIntervalCheck, urlReady } = useRouting();
  const { passiveApiFailHandler } = useErrorHandler();
  const router = useRouter();
  const contentSubpageInstance = useContentSubpageInstance();
  const { t } = useI18n();

  const populateAppSearchCardProps = (
    resourceData: AppLibraryResultItem,
  ): ResourceSearchCardProps => {
    let title = resourceData?.name ?? "";
    if (!isEmpty(resourceData?.highlights?.name)) {
      title = resourceData.highlights.name ?? "";
    }

    return {
      title: SecurityUtils.sanitizeHTMLForHighlight(title),
      type: RESOURCE_SEARCH_TYPE.APP,
      content: generateContent(resourceData),
      iconUrl: resourceData.iconUrl,
      showOpenInNewTab: resourceData.launchable && resourceData.enabled,
      ariaLabelConfig: {
        cardComponent: t("search_view_details_aria_label", {
          title: SecurityUtils.stripHtml(title),
        }),
        download: t("search_result_module_card_download_aria_label"),
      },
    };
  };

  const populateAppSearchCardPropsFromLibrary = (
    resourceData: AppLibraryResultItem,
  ): ResourceSearchCardProps => {
    let title = resourceData?.name ?? "";
    if (!isEmpty(resourceData?.highlights?.name)) {
      title = resourceData.highlights.name ?? "";
    }

    return {
      title: SecurityUtils.sanitizeHTMLForHighlight(title),
      type: RESOURCE_SEARCH_TYPE.APP,
      content: generateContent(resourceData),
      iconUrl: resourceData.iconUrl,
      showEmbed: resourceData.embeddable,
    };
  };

  const generateContent = (resourceData: AppLibraryResultItem): string => {
    const highlights = resourceData.highlights;
    if (!isEmpty(highlights.description)) {
      return (
        "[ <span>Description</span> ] " +
        SecurityUtils.sanitizeHTMLForHighlight(highlights.description)
      );
    }

    if (!isEmpty(highlights.keyword)) {
      return (
        "[ <span>Tags</span> ] " +
        SecurityUtils.sanitizeHTMLForHighlight(
          StringUtils.formatSpaceAfterComma(highlights.keyword),
        )
      );
    }

    return SecurityUtils.sanitizeHTMLForHighlight(resourceData.description ?? "");
  };

  const populateAppSearchCardListeners = (
    resourceData: AppLibraryResultItem,
  ): { click: () => void; openInNewTab: () => void } => {
    return {
      click: () => handleClick(resourceData),
      openInNewTab: () => handleOpenInNewTab(resourceData),
    };
  };

  const handleClick = (resourceData: AppLibraryResultItem): void => {
    contentSubpageInstance.show(AppDetailContentSubpage, {
      highlights: resourceData.highlights,
      uuid: resourceData?.uuid,
      hasParentSubpage: false,
    });
  };

  const handleOpenInNewTab = (resourceData: AppLibraryResultItem): void => {
    if (!resourceData.launchable || !resourceData.enabled) {
      return;
    }

    handleRouteIntervalCheck();
    AppListApi.fetchIndependentAppContext({
      params: { appUuid: resourceData.uuid, type: "LAUNCH_APP" },
    }).then((response) => {
      if (response.code === "ok") {
        const result = response.data;
        urlReady.value = result.launchUrl;

        // Track app launch event AFTER guard check
        userActivityTrackEvent.launchApp({
          appUuid: resourceData.uuid,
          appName: resourceData.name ?? "",
        });
      } else {
        const routeData = router.resolve({ name: "Forbidden" });
        urlReady.value = routeData.href;
      }
    }, passiveApiFailHandler);
  };

  return {
    populateAppSearchCardProps,
    populateAppSearchCardPropsFromLibrary,
    populateAppSearchCardListeners,
  };
};
